import { setValue } from '@truqc/content-path';
import { v4 as uuidv4 } from 'uuid';
import { post, getError } from '../../../../actions/ajax';
import notificationActions from '../../../../actions/notificationActionCreators';
import { createAtomicPatch } from '../../../../../behaviors/src/lib/atomic';
import store from '../../../../reducers/storeRegistration';
import { buildRestoredAuditLogEntry, buildSuggestionContentValue, getAuditEntryPrimaryValue, normalizeSuggestionOptionLabel, resolveSuggestionContentPath, resolveSuggestionOptionObjectKey, resolveTvcSuggestionAuditBreadcrumbs, tvcIssueErrorTypeUsesObjectValue } from './tvcIssuesSectionHelpers';
const getFormatInputParsed = (state, identifier) => {
  var _state$formType;
  if (!identifier) return null;
  const ft = (_state$formType = state.formType) === null || _state$formType === void 0 || (_state$formType = _state$formType.list) === null || _state$formType === void 0 ? void 0 : _state$formType.find(f => f.identifier === identifier);
  if (!(ft !== null && ft !== void 0 && ft.formatInput)) return null;
  try {
    return typeof ft.formatInput === 'string' ? JSON.parse(ft.formatInput) : ft.formatInput;
  } catch (_unused) {
    return null;
  }
};

/**
 * Push merge patches for one form id, optionally prefixing a single-picker atomic pre-patch.
 * @param {Array<{ id: string, patch: Object }>} patches
 * @param {string} formId
 * @param {Object|null} formatInput
 * @param {Object} patchBody
 */
const appendPatchesForForm = (patches, formId, formatInput, patchBody) => {
  if (formatInput) {
    const preMerge = createAtomicPatch(formatInput, patchBody);
    if (preMerge != null && Object.keys(preMerge).length > 0) {
      patches.push({
        id: formId,
        patch: preMerge
      });
    }
  }
  patches.push({
    id: formId,
    patch: patchBody
  });
};

/**
 * Cabinet/asset form id for a TVC check row (`applyMergePatches` target). Not the host TVC form.
 * @param {Object} item
 * @returns {string|null}
 */
export const resolveTvcIssueTargetFormId = item => {
  var _item$rowData, _item$rowData2;
  const top = item === null || item === void 0 ? void 0 : item.formID;
  if (top != null && String(top).trim() !== '') {
    return String(top).trim();
  }
  const nested = item === null || item === void 0 || (_item$rowData = item.rowData) === null || _item$rowData === void 0 ? void 0 : _item$rowData.formID;
  if (nested != null && String(nested).trim() !== '') {
    return String(nested).trim();
  }
  const fromAuditId = item === null || item === void 0 || (_item$rowData2 = item.rowData) === null || _item$rowData2 === void 0 || (_item$rowData2 = _item$rowData2.auditTrail) === null || _item$rowData2 === void 0 || (_item$rowData2 = _item$rowData2.auditTrailIdentificationSection) === null || _item$rowData2 === void 0 ? void 0 : _item$rowData2.formID;
  if (fromAuditId != null && String(fromAuditId).trim() !== '') {
    return String(fromAuditId).trim();
  }
  return null;
};

/**
 * Merge patches: `targetFormId` content field + audit trail form new log row (restore / fix).
 */
const appendTargetFormAndAuditPatches = _ref => {
  let patches = _ref.patches,
    state = _ref.state,
    targetFormId = _ref.targetFormId,
    formTypeIdentifier = _ref.formTypeIdentifier,
    auditFormId = _ref.auditFormId,
    contentPath = _ref.contentPath,
    fieldValue = _ref.fieldValue,
    logEntry = _ref.logEntry,
    newAuditEntryUuid = _ref.newAuditEntryUuid;
  const mainFormatInput = getFormatInputParsed(state, formTypeIdentifier);
  const auditFormatInput = getFormatInputParsed(state, 'auditTrail');
  appendPatchesForForm(patches, targetFormId, mainFormatInput, setValue(contentPath, {}, fieldValue));
  const auditRelPath = "/auditTrail/".concat(newAuditEntryUuid);
  appendPatchesForForm(patches, auditFormId, auditFormatInput, setValue(auditRelPath, {}, logEntry));
};

/**
 * Shared: patch cabinet row field + append audit log row. Restore and Fix differ only in how
 * `auditLogSourceEntry` / `fieldValue` are produced (historical entry vs suggestion).
 *
 * @param {Object} auditLogSourceEntry - Passed to {@link buildRestoredAuditLogEntry}
 * @returns {Array<{ id: string, patch: Object }>|null}
 */
const buildTvcIssueValueAndAuditMergePatches = (state, item, newAuditEntryUuid, userFullName, latitude, longitude, contentPath, fieldValue, auditLogSourceEntry) => {
  var _item$rowData3;
  const targetFormId = resolveTvcIssueTargetFormId(item);
  const auditFormId = item === null || item === void 0 || (_item$rowData3 = item.rowData) === null || _item$rowData3 === void 0 || (_item$rowData3 = _item$rowData3.auditTrail) === null || _item$rowData3 === void 0 ? void 0 : _item$rowData3.id;
  if (!targetFormId || !auditFormId || fieldValue == null || typeof newAuditEntryUuid !== 'string' || !newAuditEntryUuid.trim()) {
    return null;
  }
  const path = typeof contentPath === 'string' ? contentPath.trim() : '';
  if (!path) {
    return null;
  }
  const logEntry = buildRestoredAuditLogEntry(auditLogSourceEntry, userFullName, latitude, longitude);
  if (logEntry == null) {
    return null;
  }
  const patches = [];
  appendTargetFormAndAuditPatches({
    patches,
    state,
    targetFormId,
    formTypeIdentifier: item === null || item === void 0 ? void 0 : item.formTypeIdentifier,
    auditFormId,
    contentPath: path,
    fieldValue,
    logEntry,
    newAuditEntryUuid
  });
  return patches;
};

/**
 * Builds `/forms/applyMergePatches` payload: target form field + new audit trail log row.
 *
 * @param {Object} state - Redux root state
 * @param {Object} item - Issue row (`formID`, `formTypeIdentifier`, `rowData.auditTrail.id`, …)
 * @param {Object} entry - Log row to restore (`value`, and `path` or `contentPath`)
 * @param {string} newAuditEntryUuid - UUID key for the new `auditTrail.auditTrail` map entry
 * @param {string} userFullName
 * @param {number|undefined|null} latitude
 * @param {number|undefined|null} longitude
 * @returns {Array<{ id: string, patch: Object }>|null}
 */
export const buildAuditRestoreMergePatches = (state, item, entry, newAuditEntryUuid, userFullName, latitude, longitude) => {
  var _entry$path;
  if ((entry === null || entry === void 0 ? void 0 : entry.value) == null) {
    return null;
  }
  const contentPath = (_entry$path = entry.path) !== null && _entry$path !== void 0 ? _entry$path : entry.contentPath;
  return buildTvcIssueValueAndAuditMergePatches(state, item, newAuditEntryUuid, userFullName, latitude, longitude, contentPath, entry.value, entry);
};

/**
 * Builds `/forms/applyMergePatches` payload for applying one TVC suggestion value.
 *
 * @param {Object} state - Redux root state
 * @param {Object} item - Issue row (`formID`, `formTypeIdentifier`, …)
 * @param {string} typeKey - key under `rowData.suggestions`
 * @param {Object} spec - suggestion spec (`fieldType`, `contentPath`, `options`, …)
 * @param {unknown} rawOption - chosen option from `spec.options`
 * @param {Object|null} auditContext - new audit row + user/geo (same as restore)
 * @param {string} auditContext.newAuditEntryUuid
 * @param {string} [auditContext.userFullName]
 * @param {number|null|undefined} [auditContext.latitude]
 * @param {number|null|undefined} [auditContext.longitude]
 * @returns {Array<{ id: string, patch: Object }>|null}
 */
export const buildSuggestionFixMergePatches = function buildSuggestionFixMergePatches(state, item, typeKey, spec, rawOption) {
  var _auditContext$userFul;
  let auditContext = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : null;
  if (spec == null || typeof spec !== 'object' || auditContext == null || typeof auditContext.newAuditEntryUuid !== 'string' || !auditContext.newAuditEntryUuid.trim()) {
    return null;
  }
  const contentPath = resolveSuggestionContentPath(typeKey, spec, item === null || item === void 0 ? void 0 : item.errorType);
  if (contentPath == null) {
    return null;
  }
  const value = buildSuggestionContentValue(spec.fieldType, typeKey, spec, rawOption, item === null || item === void 0 ? void 0 : item.errorType);
  if (value === null) {
    return null;
  }
  const label = normalizeSuggestionOptionLabel(rawOption).trim();
  const auditLogSourceEntry = {
    path: contentPath,
    contentPath,
    value,
    displayValue: label || getAuditEntryPrimaryValue({
      value
    }),
    breadcrumbs: resolveTvcSuggestionAuditBreadcrumbs(item === null || item === void 0 ? void 0 : item.errorType, spec.breadcrumbs)
  };
  return buildTvcIssueValueAndAuditMergePatches(state, item, auditContext.newAuditEntryUuid, (_auditContext$userFul = auditContext.userFullName) !== null && _auditContext$userFul !== void 0 ? _auditContext$userFul : '', auditContext.latitude, auditContext.longitude, contentPath, value, auditLogSourceEntry);
};

/**
 * Why a suggestion fix might not produce merge patches (for console debugging).
 * @returns {{ reasons: string[], snapshot: Object }}
 */
export const collectSuggestionFixDiagnostics = (item, typeKey, spec, rawOption, auditContext) => {
  var _item$rowData4, _item$rowData5, _item$rowData6;
  const reasons = [];
  if (spec == null || typeof spec !== 'object') {
    reasons.push('invalid_spec');
  }
  if (auditContext == null) {
    reasons.push('missing_audit_context');
  } else if (typeof auditContext.newAuditEntryUuid !== 'string' || !auditContext.newAuditEntryUuid.trim()) {
    reasons.push('invalid_new_audit_entry_uuid');
  }
  const contentPath = spec != null && typeof spec === 'object' ? resolveSuggestionContentPath(typeKey, spec, item === null || item === void 0 ? void 0 : item.errorType) : null;
  if (spec != null && typeof spec === 'object' && contentPath == null) {
    reasons.push('missing_content_path');
  }
  const value = spec != null && typeof spec === 'object' ? buildSuggestionContentValue(spec.fieldType, typeKey, spec, rawOption, item === null || item === void 0 ? void 0 : item.errorType) : null;
  if (spec != null && typeof spec === 'object' && value === null) {
    reasons.push('missing_or_invalid_field_value');
  }
  const targetFormId = resolveTvcIssueTargetFormId(item);
  if (!targetFormId) {
    reasons.push('missing_target_form_id');
  }
  const auditFormId = item === null || item === void 0 || (_item$rowData4 = item.rowData) === null || _item$rowData4 === void 0 || (_item$rowData4 = _item$rowData4.auditTrail) === null || _item$rowData4 === void 0 ? void 0 : _item$rowData4.id;
  if (!auditFormId) {
    reasons.push('missing_audit_trail_form_id');
  }
  if (contentPath != null && value != null && spec != null && typeof spec === 'object') {
    var _auditContext$userFul2;
    const label = normalizeSuggestionOptionLabel(rawOption).trim();
    const auditLogSourceEntry = {
      path: contentPath,
      contentPath,
      value,
      displayValue: label || getAuditEntryPrimaryValue({
        value
      }),
      breadcrumbs: resolveTvcSuggestionAuditBreadcrumbs(item === null || item === void 0 ? void 0 : item.errorType, spec.breadcrumbs)
    };
    const logEntry = buildRestoredAuditLogEntry(auditLogSourceEntry, (_auditContext$userFul2 = auditContext === null || auditContext === void 0 ? void 0 : auditContext.userFullName) !== null && _auditContext$userFul2 !== void 0 ? _auditContext$userFul2 : '', auditContext === null || auditContext === void 0 ? void 0 : auditContext.latitude, auditContext === null || auditContext === void 0 ? void 0 : auditContext.longitude);
    if (logEntry == null) {
      reasons.push('audit_log_entry_build_failed');
    }
  }
  return {
    reasons,
    snapshot: {
      typeKey,
      specFieldType: spec === null || spec === void 0 ? void 0 : spec.fieldType,
      specContentPath: spec === null || spec === void 0 ? void 0 : spec.contentPath,
      specPath: spec === null || spec === void 0 ? void 0 : spec.path,
      resolvedContentPath: contentPath,
      itemFormID: item === null || item === void 0 ? void 0 : item.formID,
      rowDataFormID: item === null || item === void 0 || (_item$rowData5 = item.rowData) === null || _item$rowData5 === void 0 ? void 0 : _item$rowData5.formID,
      identificationFormID: item === null || item === void 0 || (_item$rowData6 = item.rowData) === null || _item$rowData6 === void 0 || (_item$rowData6 = _item$rowData6.auditTrail) === null || _item$rowData6 === void 0 || (_item$rowData6 = _item$rowData6.auditTrailIdentificationSection) === null || _item$rowData6 === void 0 ? void 0 : _item$rowData6.formID,
      resolvedTargetFormId: targetFormId !== null && targetFormId !== void 0 ? targetFormId : null,
      auditTrailFormId: auditFormId !== null && auditFormId !== void 0 ? auditFormId : null,
      errorType: item === null || item === void 0 ? void 0 : item.errorType,
      rawOptionKind: rawOption === null || rawOption === undefined ? String(rawOption) : typeof rawOption,
      expectsObjectPickerValue: tvcIssueErrorTypeUsesObjectValue(item === null || item === void 0 ? void 0 : item.errorType),
      suggestionOptionKey: (() => {
        if (value != null && typeof value === 'object' && !Array.isArray(value)) {
          const keys = Object.keys(value);
          if (keys.length === 1) return keys[0];
        }
        return resolveSuggestionOptionObjectKey(typeKey, spec, rawOption) || null;
      })()
    }
  };
};

/**
 * POST `/forms/applyMergePatches` (no `serverShow`; same for restore and fix).
 * @returns {Promise<boolean>}
 */
const postTvcIssueMergePatches = async (dispatch, patches, notifications) => {
  try {
    await post('/forms/applyMergePatches', patches);
    dispatch(notificationActions.info({
      message: notifications.successMessage,
      id: notifications.successId
    }, {
      autoClose: 4000
    }));
    return true;
  } catch (e) {
    const err = getError(e);
    dispatch(notificationActions.error({
      message: err.error_msg || notifications.failMessage,
      id: notifications.failId
    }, {
      autoClose: 6000
    }));
    return false;
  }
};

/**
 * Applies a suggestion via POST `/forms/applyMergePatches`.
 *
 * @param {Function} dispatch
 * @param {Object} item - Issue row
 * @param {string} typeKey - suggestions map key
 * @param {Object} spec
 * @param {unknown} rawOption
 * @param {string} userFullName
 * @param {number|undefined|null} latitude
 * @param {number|undefined|null} longitude
 * @returns {Promise<boolean>}
 */
export const commitSuggestionFix = async (dispatch, item, typeKey, spec, rawOption, userFullName, latitude, longitude) => {
  const newEntryId = uuidv4();
  const state = store.getState();
  const patches = buildSuggestionFixMergePatches(state, item, typeKey, spec, rawOption, {
    newAuditEntryUuid: newEntryId,
    userFullName,
    latitude,
    longitude
  });
  if (patches == null || patches.length === 0) {
    const diagnostics = collectSuggestionFixDiagnostics(item, typeKey, spec, rawOption, {
      newAuditEntryUuid: newEntryId,
      userFullName,
      latitude,
      longitude
    });
    /* eslint-disable no-console */
    console.warn('[tvcIssuesSection] suggestion fix skipped (no merge patches)', diagnostics);
    /* eslint-enable no-console */
    dispatch(notificationActions.error({
      message: 'Could not apply fix: missing target form id, audit trail, path, or value.',
      id: 'tvc_suggestion_fix_invalid'
    }, {
      autoClose: 5000
    }));
    return false;
  }
  return postTvcIssueMergePatches(dispatch, patches, {
    successMessage: 'Fix applied.',
    successId: 'tvc_suggestion_fix_ok',
    failMessage: 'Fix failed. Please try again.',
    failId: 'tvc_suggestion_fix_err'
  });
};

/**
 * Commits restore via POST `/forms/applyMergePatches` (no editor pending-save flow).
 *
 * @param {Function} dispatch - Redux dispatch
 * @param {Object} item - Issue row
 * @param {Object} entry - Audit entry to restore
 * @param {string} userFullName
 * @param {number|undefined|null} latitude
 * @param {number|undefined|null} longitude
 * @returns {Promise<boolean>} True if the request succeeded
 */
export const commitAuditTrailRestore = async (dispatch, item, entry, userFullName, latitude, longitude) => {
  const newEntryId = uuidv4();
  const state = store.getState();
  const patches = buildAuditRestoreMergePatches(state, item, entry, newEntryId, userFullName, latitude, longitude);
  if (patches == null || patches.length === 0) {
    dispatch(notificationActions.error({
      message: 'Could not restore: missing form, audit trail, or value data.',
      id: 'tvc_audit_restore_invalid'
    }, {
      autoClose: 5000
    }));
    return false;
  }
  return postTvcIssueMergePatches(dispatch, patches, {
    successMessage: 'Value restored and audit trail updated.',
    successId: 'tvc_audit_restore_ok',
    failMessage: 'Restore failed. Please try again.',
    failId: 'tvc_audit_restore_err'
  });
};